import { Button, Input } from "@aix/ui"
import { useState } from "react"
import toast from "react-hot-toast"
import { trpc } from "../../../lib/trpc"

interface QuarterlyPlanModalProps {
  coachId: string
  coachName: string
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
}

export default function QuarterlyPlanModal({
  coachId,
  coachName,
  isOpen,
  onClose,
  onSuccess,
}: QuarterlyPlanModalProps) {
  const [goals, setGoals] = useState<string[]>([""])
  const [durationWeeks, setDurationWeeks] = useState<number>(12)
  const [startDate, setStartDate] = useState<string>("")
  const [endDate, setEndDate] = useState<string>("")
  const [baselineInfo, setBaselineInfo] = useState<string>("")
  const [constraints, setConstraints] = useState<string[]>([""])

  const generateMutation = trpc.plans.quarterlyPlans.generate.useMutation({
    onSuccess: () => {
      toast.success("Quarterly plan generated successfully!")
      onSuccess()
      onClose()
      resetForm()
    },
    onError: (error) => {
      toast.error(`Failed to generate plan: ${error.message}`)
    },
  })

  const resetForm = () => {
    setGoals([""])
    setDurationWeeks(12)
    setStartDate("")
    setEndDate("")
    setBaselineInfo("")
    setConstraints([""])
  }

  const handleAddGoal = () => {
    setGoals([...goals, ""])
  }

  const handleRemoveGoal = (index: number) => {
    setGoals(goals.filter((_, i) => i !== index))
  }

  const handleGoalChange = (index: number, value: string) => {
    const newGoals = [...goals]
    newGoals[index] = value
    setGoals(newGoals)
  }

  const handleAddConstraint = () => {
    setConstraints([...constraints, ""])
  }

  const handleRemoveConstraint = (index: number) => {
    setConstraints(constraints.filter((_, i) => i !== index))
  }

  const handleConstraintChange = (index: number, value: string) => {
    const newConstraints = [...constraints]
    newConstraints[index] = value
    setConstraints(newConstraints)
  }

  const handleGenerate = () => {
    // Validation
    const validGoals = goals.filter((g) => g.trim() !== "")
    if (validGoals.length === 0) {
      toast.error("Please add at least one goal")
      return
    }
    if (!startDate) {
      toast.error("Please select a start date")
      return
    }
    if (!endDate) {
      toast.error("Please select an end date")
      return
    }

    const validConstraints = constraints.filter((c) => c.trim() !== "")

    generateMutation.mutate({
      coachId,
      goals: validGoals,
      durationWeeks,
      startDate,
      endDate,
      baselineInfo: baselineInfo.trim() || undefined,
      constraints: validConstraints.length > 0 ? validConstraints : undefined,
    })
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-xs">
      <div className="bg-background border border-border rounded-lg shadow-lg max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-background border-b border-border p-6">
          <h2 className="text-2xl font-bold text-foreground">Generate Quarterly Plan</h2>
          <p className="text-muted-foreground mt-1">
            Create a structured {durationWeeks}-week plan for {coachName}
          </p>
        </div>

        <div className="p-6 space-y-6">
          {/* Duration */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Duration (weeks)</label>
            <Input
              type="number"
              min={1}
              max={52}
              value={durationWeeks}
              onChange={(e) => setDurationWeeks(parseInt(e.target.value))}
              className="w-32"
            />
            <p className="text-xs text-muted-foreground mt-1">12 for quarterly projects, 13 for true quarters</p>
          </div>

          {/* Date Range */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Start Date</label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="w-full" />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">End Date</label>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="w-full" />
            </div>
          </div>

          {/* Goals */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Goals *</label>
            <div className="space-y-2">
              {goals.map((goal, index) => (
                <div key={index} className="flex gap-2">
                  <Input
                    type="text"
                    value={goal}
                    onChange={(e) => handleGoalChange(index, e.target.value)}
                    placeholder={`Goal ${index + 1} (e.g., "Lose 20 pounds", "Run 5k")`}
                    className="flex-1"
                  />
                  {goals.length > 1 && (
                    <Button variant="outline" onClick={() => handleRemoveGoal(index)} className="px-3">
                      ✕
                    </Button>
                  )}
                </div>
              ))}
              <Button variant="outline" onClick={handleAddGoal} className="w-full">
                + Add Goal
              </Button>
            </div>
          </div>

          {/* Baseline Info */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Baseline Info (Optional)</label>
            <textarea
              value={baselineInfo}
              onChange={(e) => setBaselineInfo(e.target.value)}
              placeholder="Describe your current state (e.g., 'Currently sedentary, haven't exercised in 6 months')"
              className="w-full min-h-[80px] px-3 py-2 bg-background border border-border rounded-md text-foreground placeholder:text-muted-foreground focus:outline-hidden focus:ring-2 focus:ring-primary"
            />
          </div>

          {/* Constraints */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Constraints (Optional)</label>
            <div className="space-y-2">
              {constraints.map((constraint, index) => (
                <div key={index} className="flex gap-2">
                  <Input
                    type="text"
                    value={constraint}
                    onChange={(e) => handleConstraintChange(index, e.target.value)}
                    placeholder={`Constraint ${index + 1} (e.g., "Can only workout in mornings", "No gym access")`}
                    className="flex-1"
                  />
                  {constraints.length > 1 && (
                    <Button variant="outline" onClick={() => handleRemoveConstraint(index)} className="px-3">
                      ✕
                    </Button>
                  )}
                </div>
              ))}
              <Button variant="outline" onClick={handleAddConstraint} className="w-full">
                + Add Constraint
              </Button>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="sticky bottom-0 bg-background border-t border-border p-6 flex gap-3 justify-end">
          <Button variant="outline" onClick={onClose} disabled={generateMutation.isLoading}>
            Cancel
          </Button>
          <Button onClick={handleGenerate} loading={generateMutation.isLoading}>
            Generate Plan
          </Button>
        </div>
      </div>
    </div>
  )
}
